import { useCallback, useEffect, useState } from 'react';

export type ToastVariant = 'default' | 'success' | 'error' | 'warning' | 'info' | 'destructive';
export type ToastPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

export interface Toast {
  id: string;
  title?: string | undefined;
  description?: string | undefined;
  variant?: ToastVariant | undefined;
  position?: ToastPosition | undefined;
  duration?: number | undefined;
  action?: {
    label: string;
    onClick: () => void;
  } | undefined;
}

interface ToastState extends Toast {
  open: boolean;
}

interface UseToastReturn {
  toasts: ToastState[];
  toast: (props: Omit<Toast, 'id'>) => void;
  dismiss: (id: string) => void;
}

// Module-level singleton store. useToast() is called from many independent
// components (any page that shows a toast, plus <Toaster/> itself) — each
// call site needs to observe the SAME toast list, not its own isolated
// useState copy, or toast() calls from a page never reach the <Toaster/>
// that's actually mounted in the DOM. Listeners re-render on every change.
let toastState: ToastState[] = [];
const listeners = new Set<(toasts: ToastState[]) => void>();

function setToastState(update: (prev: ToastState[]) => ToastState[]) {
  toastState = update(toastState);
  listeners.forEach((listener) => listener(toastState));
}

function addToast({
  title,
  description,
  variant = 'default',
  position = 'bottom-right',
  duration = 5000,
  action,
}: Omit<Toast, 'id'>): void {
  const id = Math.random().toString(36).substring(7);

  const newToast: ToastState = {
    id,
    title,
    description,
    variant,
    position,
    duration,
    action,
    open: true,
  };

  setToastState((prev) => [...prev, newToast]);

  if (duration > 0) {
    setTimeout(() => {
      setToastState((prev) => prev.map((t) => (t.id === id ? { ...t, open: false } : t)));
      setTimeout(() => {
        setToastState((prev) => prev.filter((t) => t.id !== id));
      }, 300);
    }, duration);
  }
}

function dismissToast(id: string): void {
  setToastState((prev) => prev.map((t) => (t.id === id ? { ...t, open: false } : t)));
  setTimeout(() => {
    setToastState((prev) => prev.filter((t) => t.id !== id));
  }, 300);
}

/**
 * useToast Hook
 *
 * Manages toast notifications via a module-level shared store, so any
 * component calling `toast()` is observed by the single <Toaster/>
 * mounted in the root layout — not just by itself.
 * Automatically dismisses toasts after the specified duration.
 * Supports multiple toast variants: default, success, error, warning, info, destructive
 * And positions: top-right, top-left, bottom-right, bottom-left
 *
 * @returns Object with toasts array and toast/dismiss functions
 *
 * @example
 * ```tsx
 * const { toasts, toast } = useToast();
 *
 * const handleSuccess = () => {
 *   toast({
 *     title: 'Success!',
 *     description: 'Your action was completed successfully.',
 *     variant: 'success',
 *     duration: 5000,
 *   });
 * };
 *
 * return (
 *   <>
 *     <button onClick={handleSuccess}>Show Toast</button>
 *     {toasts.map((t) => (
 *       <Toast key={t.id} {...t} onOpenChange={(open) => !open && dismiss(t.id)} />
 *     ))}
 *   </>
 * );
 * ```
 */
export function useToast(): UseToastReturn {
  const [toasts, setToasts] = useState<ToastState[]>(toastState);

  useEffect(() => {
    listeners.add(setToasts);
    setToasts(toastState);
    return () => {
      listeners.delete(setToasts);
    };
  }, []);

  const toast = useCallback((props: Omit<Toast, 'id'>) => {
    addToast(props);
  }, []);

  const dismiss = useCallback((id: string) => {
    dismissToast(id);
  }, []);

  return {
    toasts,
    toast,
    dismiss,
  };
}
